import React, { useState, useEffect, useRef } from "react";
import {
  Grid, Card, Typography, TextField, Button, Box, MenuItem,
  Switch, Chip, Divider, IconButton, Tooltip, Autocomplete,
  alpha, styled, keyframes,
} from "@mui/material";
import DirectionsBusOutlinedIcon from "@mui/icons-material/DirectionsBusOutlined";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import CloseIcon from "@mui/icons-material/Close";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import AttachMoneyOutlinedIcon from "@mui/icons-material/AttachMoneyOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import CloudUploadOutlinedIcon from "@mui/icons-material/CloudUploadOutlined";
import InsertDriveFileOutlinedIcon from "@mui/icons-material/InsertDriveFileOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import PaymentOutlinedIcon from "@mui/icons-material/PaymentOutlined";
import NotesOutlinedIcon from "@mui/icons-material/NotesOutlined";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import CircularProgress from "@mui/material/CircularProgress";
import { useFormik } from "formik";
import * as yup from "yup";
import axios from "axios";
import { useNotifier } from "../../../Core/Notifier";
import { useNavigate } from "react-router-dom";
import dayjs, { Dayjs } from "dayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { fmtCustomerName } from '../../../Core/utils/nameUtils';

// ─── Brand palette ────────────────────────────────────────────────────────────
const BRAND       = "#2f3891";
const BRAND_DARK  = "#2f3891";
const BRAND_LIGHT = "#818cf8";
const SUCCESS     = "#22c55e";
const SUCCESS_D   = "#16a34a";
const WARN        = "#f59e0b";
const DANGER      = "#ef4444";
const BG          = "#f1f5f9";
const BORDER      = "#e2e8f0";
const TEXT        = "#0f172a";
const TEXT2       = "#334155";
const MUTED       = "#64748b";

// ─── Keyframe animations ──────────────────────────────────────────────────────
const fadeSlideIn = keyframes`
  from { opacity: 0; transform: translateY(8px); }
  to   { opacity: 1; transform: translateY(0); }
`;
const pulseGlow = keyframes`
  0%, 100% { box-shadow: 0 0 0 0 ${alpha(SUCCESS, 0.35)}; }
  50%       { box-shadow: 0 0 0 8px ${alpha(SUCCESS, 0)}; }
`;
const shimmer = keyframes`
  0%   { background-position: -200% center; }
  100% { background-position: 200% center; }
`;
const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50%       { transform: translateY(-4px); }
`;

// ─── Styled Switch ─────────────────────────────────────────────────────────────
const StyledSwitch = styled(Switch)(({ theme }) => ({
  width: 48,
  height: 26,
  padding: 0,
  "& .MuiSwitch-switchBase": {
    padding: 3,
    transition: "transform 0.25s cubic-bezier(.4,0,.2,1)",
    "&.Mui-checked": {
      transform: "translateX(22px)",
      color: "#fff",
      "& .MuiSwitch-thumb": {
        boxShadow: `0 2px 8px ${alpha(BRAND, 0.5)}`,
      },
      "& + .MuiSwitch-track": {
        background: `linear-gradient(90deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
        opacity: 1,
        border: 0,
      },
    },
  },
  "& .MuiSwitch-thumb": {
    width: 20,
    height: 20,
    boxShadow: "0 1px 6px rgba(0,0,0,0.18)",
    background: "linear-gradient(135deg, #fff 0%, #f1f5f9 100%)",
    transition: "box-shadow 0.2s ease",
  },
  "& .MuiSwitch-track": {
    borderRadius: 13,
    opacity: 1,
    backgroundColor: "#cbd5e1",
    transition: theme.transitions.create(["background-color"], { duration: 250 }),
  },
}));

// ─── Shared input styles ──────────────────────────────────────────────────────
const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "12px",
    background: "#ffffff",
    transition: "all 0.2s ease",
    "& fieldset": { borderColor: BORDER, borderWidth: "1.5px" },
    "&:hover": {
      "& fieldset": { borderColor: "#a5b4fc" },
      background: "#fafbff",
    },
    "&.Mui-focused": {
      background: "#ffffff",
      "& fieldset": {
        borderColor: BRAND,
        borderWidth: "2px",
        boxShadow: `0 0 0 4px ${alpha(BRAND, 0.1)}`,
      },
    },
    "& input, & textarea": { fontSize: 14, color: TEXT },
    "& .MuiSelect-select": { fontSize: 14, color: TEXT },
  },
  "& .MuiFormHelperText-root": { marginLeft: 2 },
};

// ─── Section card ──────────────────────────────────────────────────────────────
const SECTION_ICONS_BG: Record<string, string> = {
  "Expense Details":  `linear-gradient(135deg, #2f3891 0%, #818cf8 100%)`,
  "Financial Info":   `linear-gradient(135deg, #22c55e 0%, #86efac 100%)`,
  "Category & Type":  `linear-gradient(135deg, #f59e0b 0%, #fcd34d 100%)`,
  "Payment Status":   `linear-gradient(135deg, #ef4444 0%, #fca5a5 100%)`,
  "Attachments":      `linear-gradient(135deg, #0ea5e9 0%, #7dd3fc 100%)`,
  "Additional Info":  `linear-gradient(135deg, #2f3891 0%, #c4b5fd 100%)`,
};

const SectionCard = ({
  icon, title, step, children,
}: { icon: React.ReactNode; title: string; step: number; children: React.ReactNode }) => (
  <Card
    sx={{
      borderRadius: "20px",
      background: "#ffffff",
      boxShadow: "0 1px 3px rgba(15,23,42,0.04), 0 8px 24px rgba(15,23,42,0.06)",
      border: `1px solid ${BORDER}`,
      overflow: "visible",
      mb: 2.5,
      transition: "box-shadow 0.25s ease, transform 0.25s ease",
      animation: `${fadeSlideIn} 0.4s ease both`,
      animationDelay: `${step * 0.05}s`,
      "&:hover": {
        boxShadow: "0 4px 8px rgba(15,23,42,0.05), 0 16px 40px rgba(99,102,241,0.08)",
        transform: "translateY(-1px)",
      },
    }}
  >
    {/* Card header */}
    <Box
      sx={{
        px: 3, pt: 2.5, pb: 2,
        display: "flex", alignItems: "center", gap: 1.5,
        borderBottom: "1px solid #f8fafc",
      }}
    >
      {/* Gradient icon badge */}
      <Box
        sx={{
          width: 36, height: 36, borderRadius: "10px",
          background: SECTION_ICONS_BG[title] || `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#fff", flexShrink: 0,
          boxShadow: `0 4px 10px ${alpha(BRAND, 0.28)}`,
        }}
      >
        {icon}
      </Box>

      <Box sx={{ flex: 1 }}>
        <Typography sx={{ fontWeight: 700, fontSize: 14, color: TEXT, lineHeight: 1.2 }}>{title}</Typography>
      </Box>

      {/* Step pill */}
      <Box
        sx={{
          width: 22, height: 22, borderRadius: "50%",
          background: alpha(BRAND, 0.08),
          display: "flex", alignItems: "center", justifyContent: "center",
          flexShrink: 0,
        }}
      >
        <Typography sx={{ fontSize: 10, fontWeight: 800, color: BRAND }}>{step}</Typography>
      </Box>
    </Box>

    <Box sx={{ p: 3 }}>{children}</Box>
  </Card>
);

// ─── Field label ──────────────────────────────────────────────────────────────
const FieldLabel = ({ children }: { children: React.ReactNode }) => (
  <Typography
    sx={{
      fontSize: 11, fontWeight: 700, color: MUTED, mb: 1,
      textTransform: "uppercase", letterSpacing: "0.08em",
      display: "flex", alignItems: "center", gap: 0.5,
    }}
  >
    {children}
  </Typography>
);

// ─── Toggle row ───────────────────────────────────────────────────────────────
const ToggleRow = ({
  checked, onChange, label, description, badgeOn, badgeOff,
}: {
  checked: boolean;
  onChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  label: string;
  description?: string;
  badgeOn?: { label: string; color: string };
  badgeOff?: { label: string; color: string };
}) => (
  <Box
    sx={{
      display: "flex", alignItems: "center", justifyContent: "space-between",
      p: "14px 16px",
      borderRadius: "14px",
      border: `1.5px solid ${checked ? alpha(BRAND, 0.35) : BORDER}`,
      background: checked
        ? `linear-gradient(135deg, ${alpha(BRAND, 0.04)} 0%, ${alpha(BRAND_LIGHT, 0.06)} 100%)`
        : "#fafbfc",
      cursor: "pointer",
      transition: "all 0.2s cubic-bezier(.4,0,.2,1)",
      position: "relative",
      overflow: "hidden",
      "&:hover": {
        borderColor: checked ? alpha(BRAND, 0.5) : "#c7d2fe",
        background: checked
          ? `linear-gradient(135deg, ${alpha(BRAND, 0.07)} 0%, ${alpha(BRAND_LIGHT, 0.09)} 100%)`
          : alpha(BRAND, 0.02),
        transform: "translateY(-1px)",
        boxShadow: `0 4px 12px ${alpha(BRAND, 0.1)}`,
      },
      // left accent line
      "&::before": {
        content: '""',
        position: "absolute", left: 0, top: 0, bottom: 0, width: "3px",
        borderRadius: "14px 0 0 14px",
        background: checked ? `linear-gradient(180deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)` : "transparent",
        transition: "background 0.2s ease",
      },
    }}
    onClick={() => onChange({ target: { checked: !checked } } as React.ChangeEvent<HTMLInputElement>)}
  >
    <Box sx={{ pl: checked ? 0.5 : 0, transition: "padding 0.2s" }}>
      <Typography sx={{ fontSize: 13, fontWeight: 600, color: checked ? TEXT : TEXT2, transition: "color 0.2s" }}>
        {label}
      </Typography>
      {description && (
        <Typography sx={{ fontSize: 11, color: MUTED, mt: 0.3, lineHeight: 1.4 }}>{description}</Typography>
      )}
    </Box>

    <Box display="flex" alignItems="center" gap={1.5} sx={{ flexShrink: 0 }}>
      {checked && badgeOn && (
        <Chip
          label={badgeOn.label} size="small"
          sx={{
            background: `linear-gradient(90deg, ${badgeOn.color} 0%, ${alpha(badgeOn.color, 0.8)} 100%)`,
            color: "#fff", fontWeight: 800, fontSize: 9, letterSpacing: "0.06em",
            height: 20, px: 0.5,
            boxShadow: `0 2px 6px ${alpha(badgeOn.color, 0.4)}`,
          }}
        />
      )}
      {!checked && badgeOff && (
        <Chip
          label={badgeOff.label} size="small"
          sx={{
            background: `linear-gradient(90deg, ${badgeOff.color} 0%, ${alpha(badgeOff.color, 0.75)} 100%)`,
            color: "#fff", fontWeight: 800, fontSize: 9, letterSpacing: "0.06em",
            height: 20, px: 0.5,
            boxShadow: `0 2px 6px ${alpha(badgeOff.color, 0.35)}`,
          }}
        />
      )}
      <StyledSwitch checked={checked} onChange={onChange} onClick={e => e.stopPropagation()} />
    </Box>
  </Box>
);

// ─── Drop zone ────────────────────────────────────────────────────────────────
const ZONE_COLORS: Record<string, { accent: string; bg: string; icon: string }> = {
  "Invoice / Documents": { accent: BRAND,   bg: alpha(BRAND, 0.04),   icon: alpha(BRAND, 0.3) },
  "Before Images":       { accent: WARN,    bg: alpha(WARN, 0.04),    icon: alpha(WARN, 0.4)  },
  "After Images":        { accent: SUCCESS, bg: alpha(SUCCESS, 0.04), icon: alpha(SUCCESS, 0.4) },
};

const DropZone = ({
  id, label, files, onAdd, onRemove, accept,
}: {
  id: string; label: string; files: File[];
  onAdd: (f: File[]) => void; onRemove: (i: number) => void; accept?: string;
}) => {
  const [drag, setDrag] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);
  const colors = ZONE_COLORS[label] || { accent: BRAND, bg: alpha(BRAND, 0.04), icon: alpha(BRAND, 0.3) };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault(); setDrag(false);
    onAdd(Array.from(e.dataTransfer.files));
  };

  const getFileEmoji = (name: string) => {
    if (/\.(jpg|jpeg|png|gif|webp)$/i.test(name)) return "🖼️";
    if (/\.pdf$/i.test(name)) return "📄";
    return "📎";
  };

  return (
    <Box>
      <FieldLabel>{label}</FieldLabel>

      <Box
        onDragOver={e => { e.preventDefault(); setDrag(true); }}
        onDragLeave={() => setDrag(false)}
        onDrop={handleDrop}
        onClick={() => inputRef.current?.click()}
        sx={{
          border: `2px dashed ${drag ? colors.accent : BORDER}`,
          borderRadius: "14px",
          p: "20px 16px",
          textAlign: "center",
          cursor: "pointer",
          background: drag ? colors.bg : "#fafbfc",
          transition: "all 0.2s cubic-bezier(.4,0,.2,1)",
          position: "relative",
          overflow: "hidden",
          "&:hover": {
            borderColor: colors.accent,
            background: colors.bg,
            transform: "translateY(-2px)",
            boxShadow: `0 6px 18px ${alpha(colors.accent, 0.12)}`,
          },
          // shimmer overlay on drag
          ...(drag && {
            "&::after": {
              content: '""',
              position: "absolute", inset: 0,
              background: `linear-gradient(135deg, transparent 25%, ${alpha(colors.accent, 0.06)} 50%, transparent 75%)`,
              backgroundSize: "200% 200%",
              animation: `${shimmer} 1.5s linear infinite`,
            },
          }),
        }}
      >
        <Box
          sx={{
            width: 44, height: 44, borderRadius: "12px",
            background: drag ? alpha(colors.accent, 0.15) : "#f1f5f9",
            display: "flex", alignItems: "center", justifyContent: "center",
            mx: "auto", mb: 1.25,
            transition: "all 0.2s ease",
            ...(drag && { animation: `${float} 1s ease infinite` }),
          }}
        >
          <CloudUploadOutlinedIcon sx={{ fontSize: 22, color: drag ? colors.accent : "#94a3b8", transition: "color 0.2s" }} />
        </Box>
        <Typography sx={{ fontSize: 12, color: MUTED, lineHeight: 1.5 }}>
          <Box component="span" sx={{ color: colors.accent, fontWeight: 700 }}>Click to upload</Box>
          {" "}or drag & drop
        </Typography>
        <Typography sx={{ fontSize: 10, color: "#94a3b8", mt: 0.5 }}>PDF, PNG, JPG · Max 10 MB</Typography>
      </Box>

      <input
        ref={inputRef} type="file" multiple accept={accept}
        style={{ display: "none" }} id={id}
        onChange={e => {
          const picked = e.currentTarget.files ? Array.from(e.currentTarget.files) : [];
          onAdd(picked);
          e.currentTarget.value = "";
        }}
      />

      {/* Image thumbnails */}
      {files.filter(f => /\.(jpg|jpeg|png|gif|webp)$/i.test(f.name)).length > 0 && (
        <Box sx={{ mt: 1.5, display: "flex", flexWrap: "wrap", gap: 1 }}>
          {files.map((f, i) => /\.(jpg|jpeg|png|gif|webp)$/i.test(f.name) ? (
            <Box
              key={i}
              sx={{
                position: "relative", width: 72, height: 72,
                borderRadius: "10px", overflow: "hidden",
                border: `1.5px solid ${alpha(colors.accent, 0.3)}`,
                boxShadow: `0 2px 8px ${alpha(colors.accent, 0.12)}`,
                transition: "all 0.18s ease",
                "&:hover": { transform: "scale(1.04)", boxShadow: `0 4px 14px ${alpha(colors.accent, 0.25)}` },
              }}
            >
              <Box component="img" src={URL.createObjectURL(f)} alt={f.name} sx={{ width: "100%", height: "100%", objectFit: "cover" }} />
              <IconButton size="small" onClick={e => { e.stopPropagation(); onRemove(i); }}
                sx={{ position: "absolute", top: 2, right: 2, width: 20, height: 20, bgcolor: "rgba(0,0,0,0.55)", color: "#fff", "&:hover": { bgcolor: alpha(DANGER, 0.9) } }}>
                <CloseIcon sx={{ fontSize: 11 }} />
              </IconButton>
            </Box>
          ) : null)}
        </Box>
      )}

      {/* Non-image file list */}
      {files.filter(f => !/\.(jpg|jpeg|png|gif|webp)$/i.test(f.name)).length > 0 && (
        <Box sx={{ mt: 1.5, display: "flex", flexDirection: "column", gap: 0.75 }}>
          {files.map((f, i) => !/\.(jpg|jpeg|png|gif|webp)$/i.test(f.name) ? (
            <Box
              key={i}
              sx={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                p: "8px 12px", borderRadius: "10px",
                background: `linear-gradient(135deg, #f8fafc 0%, ${alpha(colors.accent, 0.04)} 100%)`,
                border: `1px solid ${alpha(colors.accent, 0.15)}`,
                transition: "all 0.15s ease",
                "&:hover": { background: alpha(colors.accent, 0.06), borderColor: alpha(colors.accent, 0.25) },
              }}
            >
              <Box display="flex" alignItems="center" gap={1.25}>
                <Box sx={{ width: 28, height: 28, borderRadius: "7px", background: alpha(colors.accent, 0.1), display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14 }}>
                  {getFileEmoji(f.name)}
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: TEXT2, maxWidth: 120, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{f.name}</Typography>
                  <Typography sx={{ fontSize: 10, color: MUTED }}>{(f.size / 1024).toFixed(1)} KB</Typography>
                </Box>
              </Box>
              <Tooltip title="Remove file">
                <IconButton size="small" onClick={e => { e.stopPropagation(); onRemove(i); }}
                  sx={{ width: 26, height: 26, borderRadius: "7px", color: DANGER, transition: "all 0.15s ease", "&:hover": { bgcolor: alpha(DANGER, 0.1), transform: "scale(1.1)" } }}>
                  <DeleteOutlineIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            </Box>
          ) : null)}
        </Box>
      )}
    </Box>
  );
};

// ─── Main component ────────────────────────────────────────────────────────────
interface ExpenseCategory { id: number; category_name: string; }
interface WorkOrderOption  { id: number; label: string; }

const ExpenseForm = () => {
  const API_URL  = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const { showErrorMessage, showMessage } = useNotifier();

  const [isHST,     setIsHST]     = useState(false);
  const [total,     setTotal]     = useState(0);
  const [hstAmount, setHstAmount] = useState(0);
  const [loading,   setLoading]   = useState(false);
  const [expenseCategories, setExpenseCategories] = useState<ExpenseCategory[]>([]);
  const [workOrders, setWorkOrders] = useState<WorkOrderOption[]>([]);
  const [selectedTrip, setSelectedTrip] = useState<WorkOrderOption | null>(null);

  useEffect(() => {
    axios.get(`${API_URL}/view-expense-category`)
      .then(r => setExpenseCategories(r.data || []))
      .catch(() => showErrorMessage("Failed to fetch categories"));
    // Fetch work orders for trip attachment
    const token = localStorage.getItem("userToken") || localStorage.getItem("token") || "";
    axios.post(`${API_URL}/work-order/getAll`, {}, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        const data = Array.isArray(r.data?.data) ? r.data.data : [];
        setWorkOrders(data.map((wo: any) => ({
          id: wo.id,
          label: (() => { const name = fmtCustomerName(wo.customer, ""); return `${wo.quoteWorkOrderNo}${wo.InvoiceNo ? ` / ${wo.InvoiceNo}` : ""}${name ? ` — ${name}` : ""}`; })(),
        })));
      })
      .catch(() => {});
  }, []);

  const formik = useFormik({
    initialValues: {
      date:           new Date().toISOString().split("T")[0],
      description:    "",
      amount:         "",
      hst:            false,
      total:          "",
      category:       "",
      type:           "Other",
      status:         "Unpaid",
      is_installment: false,
      paid_amount:    "",
      file:           [] as File[],
      beforeImages:   [] as File[],
      afterImages:    [] as File[],
      remarks:        "",
      work_order_id:  null as number | null,
    },
    validationSchema: yup.object({
      date:        yup.date().required("Date is required"),
      description: yup.string().required("Description is required"),
      amount:      yup.number().required("Amount is required").positive("Amount must be positive"),
      category:    yup.string().required("Category is required"),
    }),
    onSubmit: async (values) => {
      setLoading(true);
      try {
        const formData = new FormData();
        formData.append("date",           values.date);
        formData.append("description",    values.description);
        formData.append("amount",         values.amount);
        formData.append("hst",            isHST ? hstAmount.toString() : "0");
        formData.append("total",          total.toString());
        formData.append("category",       values.category);
        formData.append("type",           values.type);
        formData.append("status",         values.status);
        formData.append("is_installment", values.is_installment ? "1" : "0");
        if (values.is_installment && values.paid_amount)
          formData.append("paid_amount", values.paid_amount);
        formData.append("remarks", values.remarks);
        if (values.work_order_id) formData.append("work_order_id", String(values.work_order_id));
        values.file.forEach(f         => formData.append("files[]", f));
        values.beforeImages.forEach(f => formData.append("beforeImages[]", f));
        values.afterImages.forEach(f  => formData.append("afterImages[]", f));

        await axios.post(`${API_URL}/save-expenses`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
        });

        showMessage("Expense saved successfully!");
        navigate("/expense/list");
      } catch {
        showErrorMessage("An error occurred while saving the expense.");
      } finally {
        setLoading(false);
      }
    },
  });

  // ─── Calculation handlers (logic unchanged) ────────────────────────────────
  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const inputValue = e.target.value;
    const amount = inputValue ? parseFloat(inputValue) : 0;
    formik.setFieldValue("amount", inputValue);
    if (isHST) {
      const hst = amount * 0.13;
      const upd = amount + hst;
      setHstAmount(hst);
      setTotal(parseFloat(upd.toFixed(2)));
      formik.setFieldValue("total", upd.toFixed(2));
    } else {
      setHstAmount(0);
      setTotal(amount);
      formik.setFieldValue("total", amount.toFixed(2));
    }
  };

  const handleHSTToggle = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = e.target.checked;
    setIsHST(checked);
    const curAmount = parseFloat(formik.values.amount || "0");
    const curTotal  = parseFloat(formik.values.total  || "0");
    if (checked) {
      if (curTotal > 0 && curAmount === 0) {
        const base = parseFloat((curTotal / 1.13).toFixed(2));
        const hst  = parseFloat((curTotal - base).toFixed(2));
        setHstAmount(hst);
        setTotal(parseFloat(curTotal.toFixed(2)));
        formik.setFieldValue("amount", base.toFixed(2));
      } else if (curAmount > 0) {
        const hst = parseFloat((curAmount * 0.13).toFixed(2));
        const upd = parseFloat((curAmount + hst).toFixed(2));
        setHstAmount(hst);
        setTotal(upd);
        formik.setFieldValue("total", upd.toFixed(2));
      }
    } else {
      if (curTotal > 0) {
        const base = parseFloat((curTotal / 1.13).toFixed(2));
        setHstAmount(0);
        setTotal(base);
        formik.setFieldValue("total",  base.toFixed(2));
        formik.setFieldValue("amount", base.toFixed(2));
      } else if (curAmount > 0) {
        setHstAmount(0);
        setTotal(curAmount);
        formik.setFieldValue("total", curAmount.toFixed(2));
      }
    }
  };

  const handleTotalChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const inputValue = e.target.value;
    const t = inputValue ? parseFloat(inputValue) : 0;
    formik.setFieldValue("total", inputValue);
    if (isHST) {
      const base = t / 1.13;
      const hst  = t - base;
      setHstAmount(hst);
      setTotal(t);
      formik.setFieldValue("amount", base.toFixed(2));
    } else {
      setHstAmount(0);
      setTotal(t);
      formik.setFieldValue("amount", 0);
    }
  };

  const isPaid = formik.values.status === "Paid";

  return (
    <Box
      sx={{
        minHeight: "100vh",
        pb: 6,
        // subtle dot-grid background
        background: `
          radial-gradient(circle at 20% 20%, ${alpha(BRAND, 0.04)} 0%, transparent 50%),
          radial-gradient(circle at 80% 80%, ${alpha(SUCCESS, 0.04)} 0%, transparent 50%),
          #f1f5f9
        `,
      }}
    >
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={3} alignItems="flex-start">

          {/* ════════════════ LEFT: FORM ════════════════ */}
          <Grid item xs={12} lg={8}>

            {/* ── Page header ───────────────────────────────────────── */}
            <Box sx={{ mb: 3.5, animation: `${fadeSlideIn} 0.35s ease both` }}>
              <Box display="flex" alignItems="center" gap={1.5} mb={0.75}>
                <Box
                  sx={{
                    width: 40, height: 40, borderRadius: "12px",
                    background: `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_LIGHT} 100%)`,
                    display: "flex", alignItems: "center", justifyContent: "center",
                    boxShadow: `0 6px 16px ${alpha(BRAND, 0.35)}`,
                  }}
                >
                  <AttachMoneyOutlinedIcon sx={{ color: "#fff", fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography sx={{ fontSize: 22, fontWeight: 800, color: TEXT, lineHeight: 1.2, letterSpacing: "-0.02em" }}>
                    New Expense
                  </Typography>
                  <Typography sx={{ fontSize: 13, color: MUTED, mt: 0.2 }}>
                    Fill in the details below to record a new expense entry
                  </Typography>
                </Box>
              </Box>
            </Box>

            {/* ── 1. Category & Type ────────────────────────────────── */}
            <SectionCard icon={<CategoryOutlinedIcon sx={{ fontSize: 17 }} />} title="Category & Type" step={1}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <FieldLabel>Category *</FieldLabel>
                  <TextField
                    select size="small" fullWidth
                    {...formik.getFieldProps("category")}
                    error={formik.touched.category && Boolean(formik.errors.category)}
                    helperText={formik.touched.category && formik.errors.category}
                    sx={inputSx}
                  >
                    <MenuItem value="" disabled sx={{ fontSize: 13, color: MUTED }}><em>Select a category…</em></MenuItem>
                    {expenseCategories.map(c => (
                      <MenuItem key={c.id} value={c.id} sx={{ fontSize: 13 }}>{c.category_name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <FieldLabel>Expense Type</FieldLabel>
                  <TextField select size="small" fullWidth {...formik.getFieldProps("type")} sx={inputSx}>
                    {["Other", "Labour", "Material", "Material + Labour"].map(t => (
                      <MenuItem key={t} value={t} sx={{ fontSize: 13 }}>{t}</MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12}>
                  <FieldLabel>
                    <DirectionsBusOutlinedIcon sx={{ fontSize: 13 }} />
                    Attach to Trip (optional)
                  </FieldLabel>
                  <Autocomplete
                    size="small"
                    options={workOrders}
                    value={selectedTrip}
                    onChange={(_, val) => {
                      setSelectedTrip(val);
                      formik.setFieldValue("work_order_id", val ? val.id : null);
                    }}
                    getOptionLabel={(o) => o.label}
                    isOptionEqualToValue={(a, b) => a.id === b.id}
                    renderInput={(params) => (
                      <TextField
                        {...params} placeholder="Search trip / work order…"
                        sx={inputSx}
                      />
                    )}
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "12px" } }}
                  />
                  {selectedTrip && (
                    <Typography sx={{ fontSize: 11, color: "#2f3891", mt: 0.75 }}>
                      This expense will be included in the profit calculation for trip {selectedTrip.label.split("—")[0].trim()}.
                    </Typography>
                  )}
                </Grid>
              </Grid>
            </SectionCard>

            {/* ── 2. Expense Details ────────────────────────────────── */}
            <SectionCard icon={<CalendarTodayOutlinedIcon sx={{ fontSize: 17 }} />} title="Expense Details" step={2}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={5}>
                  <FieldLabel>Date *</FieldLabel>
                  <DatePicker
                    views={["day"]}
                    openTo="day"
                    value={formik.values.date ? dayjs(formik.values.date) : null}
                    onChange={(val: Dayjs | null) => formik.setFieldValue("date", val && val.isValid() ? val.format("YYYY-MM-DD") : "")}
                    format="MMM DD, YYYY"
                    slotProps={{
                      textField: { size: "small", fullWidth: true, placeholder: "Select date", sx: {
                        ...inputSx,
                        "& .MuiOutlinedInput-root": { ...((inputSx as any)?.["& .MuiOutlinedInput-root"] || {}), borderRadius: 2.5, bgcolor: "#f9fafb", fontSize: 13, height: 40 },
                      }},
                      popper: { sx: {
                        "& .MuiPaper-root": { borderRadius: 3, boxShadow: "0 12px 40px rgba(0,0,0,0.12)", border: "1px solid #e2e8f0" },
                        "& .MuiPickersDay-root.Mui-selected": { bgcolor: "#2f3891 !important", fontWeight: 700 },
                        "& .MuiPickersDay-root:hover": { bgcolor: "#eef2ff" },
                        "& .MuiPickersDay-root": { borderRadius: 2, fontWeight: 500 },
                        "& .MuiPickersCalendarHeader-label": { fontWeight: 700, fontSize: 14, color: "#1e293b", pointerEvents: "none" },
                        "& .MuiPickersArrowSwitcher-button": { borderRadius: 1.5 },
                      }},
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <FieldLabel>Description *</FieldLabel>
                  <TextField
                    placeholder="What was this expense for? Be specific…"
                    size="small" fullWidth multiline rows={3}
                    {...formik.getFieldProps("description")}
                    error={formik.touched.description && Boolean(formik.errors.description)}
                    helperText={formik.touched.description && formik.errors.description}
                    sx={inputSx}
                  />
                </Grid>
              </Grid>
            </SectionCard>

            {/* ── 3. Financial Info ─────────────────────────────────── */}
            <SectionCard icon={<AttachMoneyOutlinedIcon sx={{ fontSize: 17 }} />} title="Financial Info" step={3}>
              <Grid container spacing={2.5}>
                <Grid item xs={12} sm={6}>
                  <FieldLabel>Amount *</FieldLabel>
                  <TextField
                    placeholder="0.00" size="small" fullWidth type="number"
                    value={formik.values.amount}
                    onChange={handleAmountChange}
                    error={formik.touched.amount && Boolean(formik.errors.amount)}
                    helperText={formik.touched.amount && formik.errors.amount}
                    InputProps={{
                      startAdornment: (
                        <Typography sx={{ color: MUTED, mr: 0.5, fontSize: 15, fontWeight: 700, userSelect: "none" }}>$</Typography>
                      ),
                    }}
                    sx={inputSx}
                  />
                </Grid>

                {/* HST toggle */}
                <Grid item xs={12}>
                  <ToggleRow
                    checked={isHST}
                    onChange={handleHSTToggle}
                    label="Include HST (13%)"
                    description="Automatically calculates 13% HST on the entered amount"
                  />
                  {isHST && (
                    <Box
                      sx={{
                        mt: 1.5,
                        px: 2.5, py: 1.5,
                        borderRadius: "12px",
                        background: `linear-gradient(135deg, ${alpha(WARN, 0.07)} 0%, ${alpha(WARN, 0.03)} 100%)`,
                        border: `1.5px solid ${alpha(WARN, 0.3)}`,
                        display: "flex", gap: 3, alignItems: "center",
                        animation: `${fadeSlideIn} 0.25s ease both`,
                      }}
                    >
                      <Box>
                        <Typography sx={{ fontSize: 10, color: MUTED, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>HST Amount</Typography>
                        <Typography sx={{ fontSize: 16, fontWeight: 800, color: WARN }}>${hstAmount.toFixed(2)}</Typography>
                      </Box>
                      <Divider orientation="vertical" flexItem />
                      <Box>
                        <Typography sx={{ fontSize: 10, color: MUTED, textTransform: "uppercase", letterSpacing: "0.06em", fontWeight: 700 }}>Rate</Typography>
                        <Typography sx={{ fontSize: 16, fontWeight: 800, color: TEXT2 }}>13%</Typography>
                      </Box>
                    </Box>
                  )}
                </Grid>

                {/* Total */}
                <Grid item xs={12} sm={6}>
                  <FieldLabel>Total Amount</FieldLabel>
                  <TextField
                    placeholder="0.00" size="small" fullWidth type="number"
                    value={total === 0 ? "" : total}
                    onChange={handleTotalChange}
                    InputProps={{
                      startAdornment: (
                        <Typography sx={{ color: SUCCESS_D, mr: 0.5, fontSize: 15, fontWeight: 700, userSelect: "none" }}>$</Typography>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderRadius: "12px",
                        background: alpha(SUCCESS, 0.04),
                        transition: "all 0.2s ease",
                        "& fieldset": { borderColor: alpha(SUCCESS, 0.4), borderWidth: "1.5px" },
                        "&:hover fieldset": { borderColor: SUCCESS },
                        "&.Mui-focused fieldset": {
                          borderColor: SUCCESS,
                          borderWidth: "2px",
                          boxShadow: `0 0 0 4px ${alpha(SUCCESS, 0.12)}`,
                        },
                        "& input": { fontSize: 17, fontWeight: 800, color: SUCCESS_D },
                      },
                    }}
                  />
                </Grid>
              </Grid>
            </SectionCard>

            {/* ── 4. Payment Status ─────────────────────────────────── */}
            <SectionCard icon={<PaymentOutlinedIcon sx={{ fontSize: 17 }} />} title="Payment Status" step={4}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <ToggleRow
                    checked={isPaid}
                    onChange={e => formik.setFieldValue("status", e.target.checked ? "Paid" : "Unpaid")}
                    label="Mark as Paid"
                    description="Toggle on if this expense has already been fully settled"
                    badgeOn={{ label: "PAID", color: SUCCESS }}
                    badgeOff={{ label: "UNPAID", color: DANGER }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <ToggleRow
                    checked={formik.values.is_installment}
                    onChange={e => formik.setFieldValue("is_installment", e.target.checked)}
                    label="Installment Payment"
                    description="Enable if this expense will be paid in multiple instalments over time"
                  />
                </Grid>
                {formik.values.is_installment && (
                  <Grid item xs={12} sm={6} sx={{ animation: `${fadeSlideIn} 0.25s ease both` }}>
                    <FieldLabel>Initial Payment Amount</FieldLabel>
                    <TextField
                      placeholder="0.00" size="small" fullWidth type="number"
                      {...formik.getFieldProps("paid_amount")}
                      InputProps={{
                        startAdornment: (
                          <Typography sx={{ color: MUTED, mr: 0.5, fontSize: 15, fontWeight: 700 }}>$</Typography>
                        ),
                      }}
                      sx={inputSx}
                    />
                  </Grid>
                )}
              </Grid>
            </SectionCard>

            {/* ── 5. Attachments ────────────────────────────────────── */}
            <SectionCard icon={<InsertDriveFileOutlinedIcon sx={{ fontSize: 17 }} />} title="Attachments" step={5}>
              <Grid container spacing={2.5}>
                {[
                  { id: "file-upload",         label: "Invoice / Documents" as const, field: "file",         arr: formik.values.file,         accept: ".pdf,.jpg,.jpeg,.png" },
                  { id: "before-image-upload",  label: "Before Images" as const,       field: "beforeImages", arr: formik.values.beforeImages, accept: ".jpg,.jpeg,.png" },
                  { id: "after-image-upload",   label: "After Images" as const,        field: "afterImages",  arr: formik.values.afterImages,  accept: ".jpg,.jpeg,.png" },
                ].map(z => (
                  <Grid item xs={12} sm={4} key={z.id}>
                    <DropZone
                      id={z.id} label={z.label}
                      files={z.arr}
                      onAdd={f => formik.setFieldValue(z.field, [...z.arr, ...f])}
                      onRemove={i => formik.setFieldValue(z.field, z.arr.filter((_, idx) => idx !== i))}
                      accept={z.accept}
                    />
                  </Grid>
                ))}
              </Grid>
            </SectionCard>

            {/* ── 6. Remarks ────────────────────────────────────────── */}
            <SectionCard icon={<NotesOutlinedIcon sx={{ fontSize: 17 }} />} title="Additional Info" step={6}>
              <FieldLabel>Remarks</FieldLabel>
              <TextField
                placeholder="Add any notes, reference numbers, or context for this expense…"
                size="small" fullWidth multiline rows={3}
                {...formik.getFieldProps("remarks")}
                sx={inputSx}
              />
            </SectionCard>

          </Grid>

          {/* ════════════════ RIGHT: SUMMARY ════════════════ */}
          <Grid item xs={12} lg={4}>
            <Box sx={{ position: { lg: "sticky" }, top: 24, animation: `${fadeSlideIn} 0.4s ease 0.1s both` }}>

              {/* ── Summary card ──────────────────────────────────── */}
              <Card
                sx={{
                  borderRadius: "20px",
                  overflow: "hidden",
                  border: `1px solid ${alpha(BRAND, 0.18)}`,
                  boxShadow: `0 8px 32px ${alpha(BRAND, 0.14)}, 0 2px 8px ${alpha(BRAND, 0.08)}`,
                  mb: 2,
                }}
              >
                {/* Gradient header with decorative blobs */}
                <Box
                  sx={{
                    background: `linear-gradient(135deg, ${BRAND} 0%, #4338ca 60%, #3730a3 100%)`,
                    px: 3, py: 3,
                    color: "#fff",
                    position: "relative",
                    overflow: "hidden",
                  }}
                >
                  {/* Decorative circles */}
                  {[
                    { w: 120, h: 120, top: -40, right: -30, opacity: 0.08 },
                    { w: 80,  h: 80,  top: 20,  right: 40,  opacity: 0.06 },
                    { w: 60,  h: 60,  bottom: -20, left: 20, opacity: 0.07 },
                  ].map((c, i) => (
                    <Box
                      key={i}
                      sx={{
                        position: "absolute",
                        width: c.w, height: c.h,
                        borderRadius: "50%",
                        background: "rgba(255,255,255,0.15)",
                        top: c.top, right: c.right, bottom: c.bottom, left: c.left,
                        opacity: c.opacity,
                        backdropFilter: "blur(1px)",
                      }}
                    />
                  ))}

                  <Box sx={{ position: "relative", zIndex: 1 }}>
                    <Typography sx={{ fontSize: 10, fontWeight: 800, letterSpacing: "0.12em", opacity: 0.75, mb: 1, textTransform: "uppercase" }}>
                      Expense Summary
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 32, fontWeight: 900, lineHeight: 1,
                        textShadow: "0 2px 8px rgba(0,0,0,0.2)",
                        fontVariantNumeric: "tabular-nums",
                      }}
                    >
                      ${total > 0 ? total.toFixed(2) : "0.00"}
                    </Typography>
                    <Typography sx={{ fontSize: 12, opacity: 0.65, mt: 0.75, fontWeight: 500 }}>
                      Grand Total · {new Date().toLocaleDateString("en-CA", { month: "short", day: "numeric", year: "numeric" })}
                    </Typography>
                  </Box>
                </Box>

                {/* Summary rows */}
                <Box sx={{ px: 3, py: 2.5 }}>
                  {[
                    {
                      label: "Subtotal",
                      value: `$${formik.values.amount ? parseFloat(formik.values.amount as string).toFixed(2) : "0.00"}`,
                    },
                    {
                      label: "HST (13%)",
                      value: isHST ? `$${hstAmount.toFixed(2)}` : "—",
                      valueColor: isHST ? WARN : MUTED,
                    },
                    {
                      label: "Status",
                      chip: { label: formik.values.status, color: isPaid ? SUCCESS : DANGER },
                    },
                    {
                      label: "Type",
                      value: formik.values.type,
                    },
                    {
                      label: "Installment",
                      value: formik.values.is_installment ? "Yes" : "No",
                      valueColor: formik.values.is_installment ? BRAND : undefined,
                    },
                    ...(formik.values.is_installment && formik.values.paid_amount
                      ? [{ label: "Initial Payment", value: `$${parseFloat(formik.values.paid_amount as string).toFixed(2)}`, valueColor: SUCCESS }]
                      : []),
                  ].map((row, i, arr) => (
                    <Box
                      key={i}
                      sx={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        py: 1.1,
                        borderBottom: i < arr.length - 1 ? "1px solid #f8fafc" : "none",
                      }}
                    >
                      <Typography sx={{ fontSize: 12, color: MUTED, fontWeight: 500 }}>{row.label}</Typography>
                      {"chip" in row && row.chip ? (
                        <Chip
                          label={row.chip.label} size="small"
                          sx={{
                            background: `linear-gradient(90deg, ${row.chip.color} 0%, ${alpha(row.chip.color, 0.8)} 100%)`,
                            color: "#fff", fontWeight: 800, fontSize: 9, letterSpacing: "0.06em",
                            height: 20,
                            boxShadow: `0 2px 6px ${alpha(row.chip.color, 0.35)}`,
                          }}
                        />
                      ) : (
                        <Typography sx={{ fontSize: 13, fontWeight: 700, color: (row as any).valueColor || TEXT2 }}>
                          {(row as any).value}
                        </Typography>
                      )}
                    </Box>
                  ))}

                  <Divider sx={{ my: 2, borderColor: "#f1f5f9" }} />

                  {/* Grand total highlight */}
                  <Box
                    sx={{
                      p: 2, borderRadius: "14px",
                      background: `linear-gradient(135deg, ${alpha(SUCCESS, 0.07)} 0%, ${alpha(SUCCESS, 0.03)} 100%)`,
                      border: `1.5px solid ${alpha(SUCCESS, 0.25)}`,
                      display: "flex", justifyContent: "space-between", alignItems: "center",
                      ...(total > 0 && { animation: `${pulseGlow} 2.5s ease infinite` }),
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1}>
                      <CheckCircleOutlineIcon sx={{ fontSize: 18, color: SUCCESS }} />
                      <Typography sx={{ fontSize: 13, fontWeight: 700, color: TEXT2 }}>Total</Typography>
                    </Box>
                    <Typography sx={{ fontSize: 22, fontWeight: 900, color: SUCCESS_D, fontVariantNumeric: "tabular-nums" }}>
                      ${total > 0 ? total.toFixed(2) : "0.00"}
                    </Typography>
                  </Box>
                </Box>
              </Card>

              {/* ── Action buttons card ────────────────────────────── */}
              <Card
                sx={{
                  borderRadius: "20px",
                  border: `1px solid ${BORDER}`,
                  boxShadow: "0 2px 8px rgba(15,23,42,0.06)",
                  p: 2.5,
                }}
              >
                {/* Submit */}
                <Button
                  type="submit" fullWidth variant="contained"
                  disabled={loading}
                  startIcon={loading ? undefined : <SaveAltIcon />}
                  sx={{
                    background: loading
                      ? "#e2e8f0"
                      : `linear-gradient(135deg, ${BRAND} 0%, ${BRAND_DARK} 50%, #3730a3 100%)`,
                    backgroundSize: "200% 200%",
                    borderRadius: "12px",
                    py: 1.6,
                    fontWeight: 700, fontSize: 14,
                    textTransform: "none",
                    letterSpacing: "0.01em",
                    border: "none",
                    boxShadow: loading ? "none" : `0 4px 16px ${alpha(BRAND, 0.4)}, 0 1px 4px ${alpha(BRAND, 0.2)}`,
                    "&:hover:not(:disabled)": {
                      background: `linear-gradient(135deg, ${BRAND_DARK} 0%, #3730a3 50%, #312e81 100%)`,
                      backgroundSize: "200% 200%",
                      boxShadow: `0 8px 24px ${alpha(BRAND, 0.5)}, 0 2px 6px ${alpha(BRAND, 0.25)}`,
                      transform: "translateY(-2px)",
                    },
                    "&:active:not(:disabled)": {
                      transform: "translateY(0)",
                      boxShadow: `0 2px 8px ${alpha(BRAND, 0.3)}`,
                    },
                    transition: "all 0.25s cubic-bezier(.4,0,.2,1)",
                    mb: 1.5,
                  }}
                >
                  {loading ? (
                    <Box display="flex" alignItems="center" gap={1}>
                      <CircularProgress size={18} sx={{ color: MUTED }} />
                      <Typography sx={{ fontSize: 13, color: MUTED }}>Saving…</Typography>
                    </Box>
                  ) : "Save Expense"}
                </Button>

                {/* Cancel */}
                <Button
                  fullWidth variant="outlined"
                  startIcon={<CloseIcon />}
                  onClick={() => navigate("/expense/list")}
                  sx={{
                    borderRadius: "12px",
                    py: 1.4, fontWeight: 600, fontSize: 13,
                    textTransform: "none",
                    borderColor: BORDER, borderWidth: "1.5px",
                    color: MUTED,
                    background: "#fafbfc",
                    "&:hover": {
                      borderColor: "#94a3b8",
                      background: "#f1f5f9",
                      color: TEXT2,
                      transform: "translateY(-1px)",
                      boxShadow: "0 2px 8px rgba(15,23,42,0.08)",
                    },
                    transition: "all 0.2s ease",
                  }}
                >
                  Cancel
                </Button>

                {/* Footer note */}
                <Box
                  sx={{
                    mt: 2.5, pt: 2, borderTop: `1px solid #f1f5f9`,
                    display: "flex", alignItems: "center", gap: 0.75, justifyContent: "center",
                  }}
                >
                  <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: BRAND, opacity: 0.5 }} />
                  <Typography sx={{ fontSize: 10, color: MUTED, textAlign: "center" }}>
                    Fields marked <strong>*</strong> are required
                  </Typography>
                  <Box sx={{ width: 5, height: 5, borderRadius: "50%", bgcolor: BRAND, opacity: 0.5 }} />
                </Box>
              </Card>

            </Box>
          </Grid>

        </Grid>
      </form>
    </Box>
  );
};

export default ExpenseForm;
